import {
  PromotionType as SharedPromotionType,
  PromotionStatus as SharedPromotionStatus,
  PROMOTION_TYPE_LABELS,
} from '@tcr-erp/shared';

export const PromotionType = SharedPromotionType;
export type PromotionType = `${SharedPromotionType}`;

export const PromotionStatus = SharedPromotionStatus;
export type PromotionStatus = `${SharedPromotionStatus}`;


// NOTE: mock shape — real API returns snake_case fields (min_order, max_discount, start_at, etc.)
export interface TenantPromotion {
  _id: string;
  code: string;
  name: string;
  type: PromotionType;
  value: number;
  minOrder?: number;
  maxDiscount?: number;
  startAt: string;
  endAt: string;
  usageLimit?: number;
  usageCount: number;
  applicableKarat?: string[];
  status: PromotionStatus;
  note?: string;
  tier_config?: Array<{ tier: string; discount_percent: number; tier_name?: string }>;
}

export interface TenantVoucher {
  _id: string;
  code: string;
  promotionId: string;
  promotionName: string;
  customerPhone?: string;
  value: number;
  used: boolean;
  usedAt?: string;
  expiresAt: string;
  createdAt: string;
}

export interface FlashSale {
  _id: string;
  name: string;
  startAt: string;
  endAt: string;
  discountPct: number;
  products: Array<{ sku: string; name: string; originalPrice: number; salePrice: number; stock: number }>;
  status: PromotionStatus;
}

export const PROMOTION_TYPE_LABEL: Record<PromotionType, string> = PROMOTION_TYPE_LABELS;

export const PROMOTION_STATUS_LABEL: Record<PromotionStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  active: 'Đang chạy',
  paused: 'Tạm dừng',
  expired: 'Hết hạn',
  cancelled: 'Đã huỷ',
  rejected: 'Bị từ chối',
};

export const PROMOTION_STATUS_COLOR: Record<PromotionStatus, string> = {
  draft: 'default',
  pending: 'blue',
  active: 'green',
  paused: 'orange',
  expired: 'red',
  cancelled: 'red',
  rejected: 'red',
};

const baseDate = (offset: number) => {
  const d = new Date(2026, 3, 1);
  d.setDate(d.getDate() + offset);
  return d.toISOString();
};

export const mockPromotions: TenantPromotion[] = [
  {
    _id: 'pr-01', code: 'TANG5PC', name: 'Giảm 5% toàn đơn cuối tuần',
    type: 'percentage_discount', value: 5, minOrder: 10_000_000,
    startAt: baseDate(-10), endAt: baseDate(20),
    usageCount: 42, usageLimit: 500,
    status: 'active',
  },
  {
    _id: 'pr-02', code: 'SJC-500K', name: 'Giảm 500K SJC ≥ 5 chỉ',
    type: 'fixed_amount', value: 500_000, minOrder: 40_000_000,
    applicableKarat: ['SJC'], startAt: baseDate(-20), endAt: baseDate(10),
    usageCount: 18, usageLimit: 100,
    status: 'active',
  },
  {
    _id: 'pr-03', code: 'BOGO-18K', name: 'Mua nhẫn 18K tặng bông tai bạc',
    type: 'bxgy', value: 1, applicableKarat: ['18K'],
    startAt: baseDate(-5), endAt: baseDate(25),
    usageCount: 7,
    status: 'active',
  },
  {
    _id: 'pr-04', code: 'VIP-DISCOUNT', name: 'Chiết khấu hạng thành viên',
    type: 'tier_discount', value: 5,
    tier_config: [
      { tier: 'tier-bronze', tier_name: 'Đồng', discount_percent: 3 },
      { tier: 'tier-silver', tier_name: 'Bạc', discount_percent: 5 },
      { tier: 'tier-gold', tier_name: 'Vàng', discount_percent: 10 },
      { tier: 'tier-diamond', tier_name: 'Kim Cương', discount_percent: 15 },
    ],
    startAt: baseDate(-40), endAt: baseDate(15),
    usageCount: 23,
    status: 'active',
  },
  {
    _id: 'pr-05', code: 'NEWYEAR2027', name: 'Giảm 10% đầu năm — sắp triển khai',
    type: 'percentage_discount', value: 10, minOrder: 5_000_000,
    startAt: baseDate(180), endAt: baseDate(210),
    usageCount: 0, usageLimit: 1000,
    status: 'draft',
  },
];

export const mockVouchers: TenantVoucher[] = [
  {
    _id: 'vc-01', code: 'VIP-A8XK2P', promotionId: 'pr-01', promotionName: 'Giảm 5% toàn đơn',
    customerPhone: '0901234001', value: 500_000,
    used: false, expiresAt: baseDate(20), createdAt: baseDate(-10),
  },
  {
    _id: 'vc-02', code: 'VIP-B9YL3Q', promotionId: 'pr-02', promotionName: 'Giảm 500K SJC',
    customerPhone: '0901234003', value: 500_000,
    used: true, usedAt: baseDate(-5), expiresAt: baseDate(10), createdAt: baseDate(-20),
  },
  {
    _id: 'vc-03', code: 'NEW-C1ZM4R', promotionId: 'pr-01', promotionName: 'Giảm 5% toàn đơn',
    value: 1_000_000, used: false, expiresAt: baseDate(25), createdAt: baseDate(-3),
  },
  {
    _id: 'vc-04', code: 'GIFT-D2AN5S', promotionId: 'pr-04', promotionName: 'Nhân đôi điểm sinh nhật',
    customerPhone: '0901234009', value: 200_000,
    used: true, usedAt: baseDate(-20), expiresAt: baseDate(-15), createdAt: baseDate(-40),
  },
];

export const mockFlashSales: FlashSale[] = [
  {
    _id: 'fs-01', name: 'Flash Sale thứ 7 — 18K',
    startAt: baseDate(2), endAt: baseDate(2),
    discountPct: 8,
    products: [
      { sku: 'NH18K-A01', name: 'Nhẫn cưới 18K #A01', originalPrice: 5_400_000, salePrice: 4_968_000, stock: 5 },
      { sku: 'BT18K-C12', name: 'Bông tai 18K #C12', originalPrice: 1_600_000, salePrice: 1_472_000, stock: 10 },
    ],
    status: 'draft',
  },
];
